import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateMcpTool } from "../api/useCreateMcpTool";
import { useUpdateMcpTool } from "../api/useUpdateMcpTool";
import { McpTool } from "../models/McpTool";

interface McpToolFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tool?: McpTool; // If provided, it's edit mode
}

export function McpToolFormModal({
  open,
  onClose,
  onSuccess,
  tool,
}: McpToolFormModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    authorizationToken: "",
  });
  const [tokenChanged, setTokenChanged] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { createMcpTool, isCreating } = useCreateMcpTool();
  const { updateMcpTool, isUpdating } = useUpdateMcpTool();

  const isEdit = !!tool;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (open) {
      if (tool) {
        // Edit mode - populate form with existing data
        // Show placeholder for obfuscated token
        setFormData({
          name: tool.name,
          url: tool.url,
          authorizationToken: "",
        });
        setTokenChanged(false);
      } else {
        // Add mode - reset form
        setFormData({
          name: "",
          url: "",
          authorizationToken: "",
        });
        setTokenChanged(false);
      }
      setErrors({});
    }
  }, [open, tool]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.url.trim()) {
      newErrors.url = "URL is required";
    } else {
      // Basic URL validation
      try {
        const url = new URL(formData.url);
        if (!url.protocol.startsWith("https")) {
          newErrors.url = "URL must start with https://";
        }
      } catch {
        newErrors.url = "Please enter a valid URL";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEdit && tool) {
        if (tokenChanged) {
          // Token was changed, send the new value (or empty string to clear it)
          await updateMcpTool(
            tool.name,
            formData.name,
            formData.url,
            formData.authorizationToken || "",
          );
        } else {
          // Token wasn't changed, don't send it at all
          await updateMcpTool(tool.name, formData.name, formData.url);
        }
      } else {
        await createMcpTool(
          formData.name,
          formData.url,
          formData.authorizationToken || undefined,
        );
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving MCP tool:", error);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Track if authorization token was changed in edit mode
    if (field === "authorizationToken" && isEdit) {
      setTokenChanged(true);
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit MCP Tool" : "Add New MCP Tool"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Enter tool name"
              disabled={isEdit || isLoading}
              required
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="url">URL *</Label>
            <Input
              id="url"
              value={formData.url}
              onChange={(e) => handleInputChange("url", e.target.value)}
              placeholder="Enter tool URL"
              disabled={isLoading}
              required
            />
            {errors.url && (
              <p className="text-red-500 text-sm mt-1">{errors.url}</p>
            )}
          </div>

          <div>
            <Label htmlFor="authorizationToken">Authorization Token</Label>
            {isEdit && tool?.authorizationToken && (
              <div className="mb-2">
                <Label className="text-sm text-gray-600">Current Token:</Label>
                <div className="bg-gray-100 border rounded-md px-3 py-2 text-sm font-mono text-gray-700">
                  {tool.authorizationToken}
                </div>
              </div>
            )}
            <Input
              id="authorizationToken"
              type="password"
              value={formData.authorizationToken}
              onChange={(e) =>
                handleInputChange("authorizationToken", e.target.value)
              }
              placeholder={
                isEdit
                  ? "Enter new token to replace current one (optional)"
                  : "Enter authorization token (optional)"
              }
              disabled={isLoading}
            />
            {isEdit && (
              <p className="text-gray-500 text-sm mt-1">
                Leave empty to keep the current token, or enter a new token to
                replace it.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? isEdit
                  ? "Updating..."
                  : "Creating..."
                : isEdit
                  ? "Update Tool"
                  : "Create Tool"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
